/**
 * Validation utilities for Excel file uploads and field validation
 */

// Allowed MIME types for Excel files
const ALLOWED_EXCEL_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel" // .xls
];

// Allowed file extensions
const ALLOWED_EXCEL_EXTENSIONS = [".xlsx", ".xls"];

// Maximum file size (5 MB)
export const MAX_FILE_SIZE = 20 * 1024 * 1024;

/**
 * Validates if a file is a valid Excel file based on MIME type and extension
 */
export const isValidExcelFile = (file: File): boolean => {
  // Check MIME type
  const hasValidMimeType = ALLOWED_EXCEL_MIME_TYPES.includes(file.type);

  // Check file extension
  const hasValidExtension = ALLOWED_EXCEL_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  );

  return hasValidMimeType && hasValidExtension;
};

/**
 * Validates if a file size is within acceptable limits
 */
export const isValidFileSize = (file: File): boolean => {
  return file.size <= MAX_FILE_SIZE;
};

/**
 * Validates if all expected headers are present in the uploaded file
 */
export const validateExcelHeaders = (
  actualHeaders: string[],
  expectedHeaders: string[]
): { isValid: boolean; missingHeaders: string[] } => {
  const missingHeaders = expectedHeaders.filter((h) => !actualHeaders.includes(h));

  return {
    isValid: missingHeaders.length === 0,
    missingHeaders
  };
};

/**
 * Gets a user-friendly file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Comprehensive file validation result
 */
export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates a file comprehensively
 */
export const validateFile = (file: File): FileValidationResult => {
  const errors: string[] = [];

  // Check if file exists
  if (!file) {
    errors.push("No file provided");
    return { isValid: false, errors };
  }

  // Check file size
  if (!isValidFileSize(file)) {
    errors.push(
      `File is too large. Maximum size is ${formatFileSize(
        MAX_FILE_SIZE
      )}, but file is ${formatFileSize(file.size)}`
    );
  }

  // Check file type
  if (!isValidExcelFile(file)) {
    errors.push("Invalid file type. Please upload a valid Excel file (.xlsx or .xls)");
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
